import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { alert, confirm, haptic } from '../lib/telegram';
import { formatAmount } from '../lib/format';
import type { Currency, Summary } from '../lib/types';

interface Props {
  onDone: () => void;
  onBack: () => void;
}

// Physical denominations people actually hold, largest first.
// SYP: current banknote series. USD: bills — coins are rare here, so any
// sub-dollar remainder just surfaces in the difference line.
const DENOMINATIONS: Record<Currency, number[]> = {
  SYP: [50000, 20000, 10000, 5000, 2500, 2000, 1000, 500],
  USD: [100, 50, 20, 10, 5, 1],
};

const ORDER: Currency[] = ['SYP', 'USD'];
const EPSILON = 0.005; // ignore sub-cent float noise

export default function CashCounter({ onDone, onBack }: Props) {
  const [summary, setSummary] = useState<Summary | null>(null);
  // Raw string per denomination so a field can be genuinely empty.
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.summary()
      .then(setSummary)
      .catch((e) => setErr(e instanceof Error ? e.message : 'failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const keyOf = (cur: Currency, denom: number) => `${cur}:${denom}`;
  const countFor = (cur: Currency, denom: number) =>
    Math.max(0, parseInt(counts[keyOf(cur, denom)] || '0', 10) || 0);

  function setCount(cur: Currency, denom: number, raw: string) {
    const cleaned = raw.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '');
    setCounts((c) => ({ ...c, [keyOf(cur, denom)]: cleaned }));
  }

  function bump(cur: Currency, denom: number, delta: number) {
    const next = Math.max(0, countFor(cur, denom) + delta);
    // Reset to empty (not "0") at zero so a net-zero field reads as *not counted*.
    setCounts((c) => ({ ...c, [keyOf(cur, denom)]: next === 0 ? '' : String(next) }));
    haptic('light');
  }

  const countedTotal = (cur: Currency) =>
    DENOMINATIONS[cur].reduce((sum, d) => sum + d * countFor(cur, d), 0);

  // "Counted" = the user actually entered at least one note for this currency.
  // A currency with no notes is never reconciled, so it can't be zeroed out.
  const counted = (cur: Currency) => DENOMINATIONS[cur].some((d) => countFor(cur, d) > 0);

  const diffFor = (cur: Currency): number | null =>
    summary ? countedTotal(cur) - summary.balances[cur] : null;

  const adjustable = (cur: Currency) => {
    const d = diffFor(cur);
    return counted(cur) && d !== null && Math.abs(d) >= EPSILON;
  };

  const anyCounted = ORDER.some(counted);
  const anyAdjustable = ORDER.some(adjustable);

  async function reconcileToCount() {
    setBusy(true);
    setErr(null);
    try {
      // Re-read the live balance so the confirmation and the adjustment reflect
      // reality, not a possibly-stale mount-time snapshot (entries can arrive via
      // the bot / recurring jobs while cash is being counted).
      const fresh = await api.summary();
      setSummary(fresh);

      // Only currencies actually counted, and only where they differ.
      const targets = ORDER
        .filter(counted)
        .map((cur) => ({ cur, total: countedTotal(cur), diff: countedTotal(cur) - fresh.balances[cur] }))
        .filter((t) => Math.abs(t.diff) >= EPSILON);

      if (targets.length === 0) {
        haptic('success');
        alert('Your count already matches your balance — nothing to adjust.');
        return;
      }

      const lines = targets.map(({ cur, total, diff }) => {
        const sign = diff < 0 ? '−' : '+';
        return `${cur}: ${formatAmount(total, cur)} (${sign}${formatAmount(Math.abs(diff), cur)})`;
      });
      const ok = await confirm(`Adjust your balance to match the count?\n\n${lines.join('\n')}`);
      if (!ok) return;

      const usd = targets.find((t) => t.cur === 'USD')?.total;
      const syp = targets.find((t) => t.cur === 'SYP')?.total;
      const res = await api.reconcile(usd, syp);
      haptic('success');
      alert(res.message ?? `Balance updated — ${res.entries.length} adjustment(s) made.`);
      onDone();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to reconcile');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>Cash counter</h1>
      </div>
      {err && <div className="error">{err}</div>}

      {loading ? (
        <div className="card"><div className="muted">Loading…</div></div>
      ) : !summary ? (
        <div className="card">
          <div className="muted">Couldn't load your balance. Go back and open Cash counter again.</div>
        </div>
      ) : (
        <>
          <div className="card">
            <p className="muted small" style={{ margin: 0 }}>
              Count the notes you physically hold of each denomination. The total is
              checked against your tracked balance so you can catch a mismatch — and
              fix it in one tap.
            </p>
          </div>

          {ORDER.map((cur) => {
            const total = countedTotal(cur);
            return (
              <div className="card" key={cur}>
                <div className="section-title"><span>{cur} cash</span></div>

                {DENOMINATIONS[cur].map((denom) => {
                  const n = countFor(cur, denom);
                  const sub = denom * n;
                  return (
                    <div className="denom-row" key={denom}>
                      <div className="denom-face">{denom.toLocaleString('en-US')}</div>
                      <div className="denom-stepper">
                        <button
                          type="button"
                          className="stepper-btn"
                          aria-label={`one fewer ${denom} ${cur} note`}
                          disabled={n === 0}
                          onClick={() => bump(cur, denom, -1)}
                        >−</button>
                        <input
                          className="denom-count"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          placeholder="0"
                          value={counts[keyOf(cur, denom)] ?? ''}
                          onChange={(e) => setCount(cur, denom, e.target.value)}
                        />
                        <button
                          type="button"
                          className="stepper-btn"
                          aria-label={`one more ${denom} ${cur} note`}
                          onClick={() => bump(cur, denom, +1)}
                        >+</button>
                      </div>
                      <div className={`denom-sub${sub === 0 ? ' zero' : ''}`}>
                        {sub === 0 ? '—' : formatAmount(sub, cur)}
                      </div>
                    </div>
                  );
                })}

                <div className="count-total-line">
                  <span className="ct-label">Counted total</span>
                  <span className="ct-value">{formatAmount(total, cur)}</span>
                </div>
                <CountDiff
                  diff={diffFor(cur)}
                  counted={counted(cur)}
                  currency={cur}
                  appBalance={summary.balances[cur]}
                />
              </div>
            );
          })}

          {anyCounted && (
            <>
              <button className="btn" disabled={busy || !anyAdjustable} onClick={reconcileToCount}>
                {busy
                  ? 'Updating…'
                  : anyAdjustable
                    ? 'Reconcile balance to match count'
                    : '✓ Balance matches your count'}
              </button>
              <button
                className="btn btn-secondary"
                style={{ marginTop: 8 }}
                disabled={busy}
                onClick={() => setCounts({})}
              >
                Clear counts
              </button>
            </>
          )}
        </>
      )}
    </>
  );
}

function CountDiff({
  diff, counted, currency, appBalance,
}: { diff: number | null; counted: boolean; currency: Currency; appBalance: number }) {
  if (diff === null) return null;
  if (!counted) {
    return <div className="count-diff muted">App balance: {formatAmount(appBalance, currency)}</div>;
  }
  if (Math.abs(diff) < EPSILON) {
    return <div className="count-diff amount-pos">✓ Matches your balance ({formatAmount(appBalance, currency)})</div>;
  }
  // diff > 0 → more cash on hand than the app tracked; diff < 0 → less.
  const cls = diff < 0 ? 'amount-neg' : 'amount-pos';
  const label = diff < 0
    ? `Short ${formatAmount(Math.abs(diff), currency)} vs balance`
    : `Over ${formatAmount(diff, currency)} vs balance`;
  return (
    <div className={`count-diff ${cls}`}>
      {label} <span className="muted">(app shows {formatAmount(appBalance, currency)})</span>
    </div>
  );
}
